import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload as UploadIcon, FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RechargeDialog } from "@/components/wallet/RechargeDialog";

const CATEGORIES = ["Diabetes", "Thyroid", "Cardiac", "Kidney", "Liver", "Vitamins", "Lipid", "CBC / Haematology", "Full body", "Custom"];

export default function PatientUpload() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [family, setFamily] = useState([]);
  const [form, setForm] = useState({
    patient_name: "",
    patient_age: "",
    patient_gender: "male",
    report_date: "",
    package_name: "",
    family_member_id: "self",
    category: "auto",
  });
  // ---- Recharge modal state ----
  // When the backend returns 402 (insufficient coins) we DO NOT navigate away.
  // Instead we open a recharge dialog and keep the file + form in state so
  // that once the wallet is topped up we can auto-resume the same upload.
  const [recharge, setRecharge] = useState({ open: false, balance: 0, required: 0 });
  const pendingFdRef = useRef(null);

  useEffect(() => { api.get("/family").then((r) => setFamily(r.data || [])).catch(() => {}); }, []);

  const onPickFamily = (val) => {
    setForm((f) => {
      if (val === "self") return { ...f, family_member_id: "self" };
      const m = family.find((x) => x.id === val);
      if (!m) return { ...f, family_member_id: val };
      return {
        ...f,
        family_member_id: val,
        patient_name: m.full_name || f.patient_name,
        patient_age: m.age || f.patient_age,
        patient_gender: m.gender || f.patient_gender,
      };
    });
  };

  const buildFormData = () => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => {
      if (!v) return;
      if (k === "family_member_id" && v === "self") return;  // skip self placeholder
      if (k === "category" && v === "auto") return;           // let AI infer
      fd.append(k, String(v));
    });
    return fd;
  };

  // Send an already-built FormData (or build fresh from state). Split from
  // `submit` so we can retry after a successful recharge without asking the
  // user to pick the file again.
  const sendUpload = async (fd) => {
    setBusy(true);
    try {
      const r = await api.post("/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report uploaded — AI is analyzing…");
      pendingFdRef.current = null;
      nav(`/report/${r.data.id}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        const d = err.response.data?.detail || {};
        const bal = Number(d.balance ?? 0);
        const req = Number(d.required ?? 15);
        // Keep the FormData in memory so we can resend it right after payment.
        pendingFdRef.current = fd;
        setRecharge({ open: true, balance: bal, required: req });
      } else {
        toast.error(apiError(err, "Upload failed"));
      }
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a report file"); return; }
    if (!form.patient_name) { toast.error("Enter patient name"); return; }
    await sendUpload(buildFormData());
  };

  // Fired by RechargeDialog once the wallet balance covers the report cost.
  const onRechargeSuccess = async () => {
    setRecharge({ open: false, balance: 0, required: 0 });
    // Rebuild fresh FormData because the original FormData stream may have
    // been consumed by axios during the failed request.
    const fd = pendingFdRef.current ? buildFormData() : null;
    if (!fd) { toast.error("Please re-select your file to continue."); return; }
    toast.success("Wallet topped up — resuming your report…");
    await sendUpload(fd);
  };

  return (
    <DashboardLayout role="patient">
      <div className="max-w-3xl">
        <h1 className="font-heading font-bold text-3xl text-ink">Upload a report</h1>
        <p className="text-ink/60">PDF, JPG or PNG.</p>

        <Card className="mt-6 p-6 rounded-2xl">
          <form onSubmit={submit} className="space-y-5">
            <label data-testid="upload-dropzone" className="block border-2 border-dashed border-border rounded-2xl p-10 text-center cursor-pointer hover:border-brand hover:bg-brand-light/30 transition-all">
              <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
              <FileUp className="w-10 h-10 mx-auto text-brand" />
              <div className="mt-3 font-heading font-semibold text-ink">{file ? file.name : "Click to choose file"}</div>
              <div className="text-xs text-ink/50">or drag &amp; drop</div>
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>For whom is this report?</Label>
                <Select value={form.family_member_id} onValueChange={onPickFamily}>
                  <SelectTrigger data-testid="upload-for-whom"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {family.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.relation})</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-ink/50 mt-1">Add family members from <a href="/patient/family" className="underline">Family Members</a> to share reports.</p>
              </div>
              <div><Label>Patient name *</Label><Input data-testid="upload-patient-name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} required /></div>
              <div><Label>Age</Label><Input type="number" value={form.patient_age} onChange={(e) => setForm({ ...form, patient_age: e.target.value })} /></div>
              <div>
                <Label>Gender</Label>
                <Select value={form.patient_gender} onValueChange={(v) => setForm({ ...form, patient_gender: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Report date</Label><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-detect (recommended)</SelectItem>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Package / test panel</Label><Input value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} placeholder="e.g. Comprehensive Health Checkup" /></div>
              <div><Label>Patient email <span className="text-ink/40">(for delivery)</span></Label><Input type="email" data-testid="upload-patient-email" value={form.patient_email || ""} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} placeholder="patient@example.com" /></div>
              <div><Label>WhatsApp number <span className="text-ink/40">(for delivery)</span></Label><Input data-testid="upload-patient-whatsapp" value={form.patient_whatsapp || ""} onChange={(e) => setForm({ ...form, patient_whatsapp: e.target.value.replace(/[^0-9+]/g, "") })} placeholder="+91 98765 43210" inputMode="tel" /></div>
            </div>

            <Button data-testid="upload-submit" type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-dark text-white">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><UploadIcon className="w-4 h-4 mr-2" /> Upload &amp; analyze</>}
            </Button>
          </form>
        </Card>

        <p className="mt-4 text-xs text-ink/50 text-center">By uploading, you agree to EFGH&apos;s medical disclaimer. This is not medical advice.</p>
      </div>

      <RechargeDialog
        open={recharge.open}
        onClose={() => setRecharge({ open: false, balance: 0, required: 0 })}
        onSuccess={onRechargeSuccess}
        balance={recharge.balance}
        required={recharge.required}
      />
    </DashboardLayout>
  );
}
